import * as React from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "lib/utils";
import { Skeleton } from "components/ui/skeleton";
import { IconTile } from "components/ui/icon-tile";
import { StatCard, StatCardGrid, type StatCardProps } from "components/ui/stat-card";
import { type Tone } from "components/ui/tone";

/* ══════════════════════════════════════════════════════════════════════════
   Page anatomy

     <Page>
       <PageHeader … />        title · description · breadcrumb · actions
       <PageStats … />         optional metric row
       <Toolbar … />           optional search / filters
       <PageSection>…          content
     </Page>

   One anatomy for every screen. Previously each feature owned a *Header.tsx
   that re-declared its own icon colours, KPI grid and toolbar, which is why
   spacing and type sizes drifted between Campaigns, Leads and Templates.
   ══════════════════════════════════════════════════════════════════════════ */

export interface PageProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Caps the reading width. `full` for tables and dashboards. */
    width?: "full" | "wide" | "narrow";
    /** Removes the outer padding for full-bleed canvases (editor, flow) */
    bleed?: boolean;
}

const WIDTH: Record<NonNullable<PageProps["width"]>, string> = {
    full: "max-w-none",
    wide: "max-w-[110rem]",
    narrow: "",
};

export const Page: React.FC<PageProps> = ({
    width = "wide",
    bleed = false,
    className,
    children,
    ...props
}) => (
    <div
        className={cn(
            "w-full mx-auto min-w-0",
            WIDTH[width],
            !bleed && "px-4 sm:px-6 lg:px-8 py-5 sm:py-6",
            "flex flex-col gap-5",
            className
        )}
        {...props}
    >
        {children}
    </div>
);

/* ── Breadcrumb ───────────────────────────────────────────────────────── */

export interface Crumb {
    label: string;
    to?: string;
}

export const Breadcrumb: React.FC<{ items: Crumb[]; className?: string }> = ({ items, className }) => {
    if (!items?.length) return null;
    return (
        <nav aria-label="Breadcrumb" className={cn("min-w-0", className)}>
            <ol className="flex items-center gap-1 text-meta font-medium text-content-muted min-w-0 flex-wrap">
                {items.map((crumb, i) => {
                    const last = i === items.length - 1;
                    return (
                        <li key={`${crumb.label}-${i}`} className="flex items-center gap-1 min-w-0">
                            {i > 0 && (
                                <ChevronRight className="w-3 h-3 flex-shrink-0 opacity-60" aria-hidden="true" />
                            )}
                            {crumb.to && !last ? (
                                <Link
                                    to={crumb.to}
                                    className="truncate rounded hover:text-content transition-colors focus-ring"
                                >
                                    {crumb.label}
                                </Link>
                            ) : (
                                <span
                                    className={cn("truncate", last && "text-content-secondary")}
                                    aria-current={last ? "page" : undefined}
                                >
                                    {crumb.label}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};

/* ── PageHeader ───────────────────────────────────────────────────────── */

export interface PageHeaderProps {
    /** Lucide component — the tone supplies the colour, so no colour props */
    icon?: React.ElementType;
    tone?: Tone;
    title: React.ReactNode;
    /** One line explaining what this screen is for */
    description?: React.ReactNode;
    /** Small pill next to the title — a count, a plan tier, a status */
    badge?: React.ReactNode;
    breadcrumb?: Crumb[];
    /** Primary + secondary buttons, right aligned on desktop */
    actions?: React.ReactNode;
    /** Rendered under the title block — tabs, segmented control */
    below?: React.ReactNode;
    /**
     * Slot before the icon — a back control on a sub-page.
     *
     * The shell's breadcrumb already links to the section root; this is for the
     * cases where a page can return somewhere more precise (the list *at the
     * page you left it*, or a modal's close). Pages that only need "back to the
     * list" should rely on the breadcrumb rather than adding a second control.
     */
    leading?: React.ReactNode;
    isLoading?: boolean;
    /** Hides the icon tile for dense sub-pages */
    compact?: boolean;
    className?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
    icon: Icon,
    tone = "brand",
    title,
    description,
    badge,
    breadcrumb,
    actions,
    below,
    leading,
    isLoading = false,
    compact = false,
    className,
}) => (
    <header className={cn("w-full min-w-0 flex flex-col gap-3", className)}>
        {breadcrumb && <Breadcrumb items={breadcrumb} />}

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 min-w-0">
            <div className="flex items-start gap-3 min-w-0 flex-1">
                {leading}
                {Icon && !compact && (
                    <IconTile tone={tone} size="lg" className="mt-0.5">
                        <Icon strokeWidth={2} />
                    </IconTile>
                )}

                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h1 className="text-title-lg sm:text-display-sm font-bold text-content tracking-tight leading-tight min-w-0">
                            {title}
                        </h1>
                        {badge != null &&
                            (isLoading ? (
                                <Skeleton className="h-5 w-14 rounded-full" />
                            ) : typeof badge === "string" || typeof badge === "number" ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-micro font-semibold bg-surface-sunken text-content-secondary border border-line flex-shrink-0 tabular-nums">
                                    {badge}
                                </span>
                            ) : (
                                badge
                            ))}
                    </div>

                    {description && (
                        <p className="text-ui text-content-muted leading-relaxed mt-1 max-w-2xl">
                            {description}
                        </p>
                    )}
                </div>
            </div>

            {actions && (
                <div
                    className={cn(
                        "flex items-center gap-2 flex-shrink-0 min-w-0",
                        // On phones the actions become a full-width row so the
                        // primary CTA is reachable with a thumb.
                        "w-full sm:w-auto [&>*]:flex-1 sm:[&>*]:flex-none"
                    )}
                >
                    {actions}
                </div>
            )}
        </div>

        {below}
    </header>
);

/* ── PageStats ────────────────────────────────────────────────────────── */

export interface PageStat extends Omit<StatCardProps, "isLoading"> {
    id?: string;
}

export const PageStats: React.FC<{
    stats: PageStat[];
    isLoading?: boolean;
    columns?: 2 | 3 | 4 | 5 | 6;
    className?: string;
}> = ({ stats, isLoading, columns, className }) => {
    if (!stats?.length) return null;
    const cols = (columns ?? Math.min(Math.max(stats.length, 2), 6)) as 2 | 3 | 4 | 5 | 6;
    return (
        <StatCardGrid columns={cols} className={className}>
            {stats.map((stat, i) => (
                <StatCard key={stat.id ?? i} {...stat} isLoading={isLoading} />
            ))}
        </StatCardGrid>
    );
};

/* ── PageSection / Panel ──────────────────────────────────────────────── */

export interface PageSectionProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
    title?: React.ReactNode;
    description?: React.ReactNode;
    actions?: React.ReactNode;
    /** Wraps the body in a surface card. Off for bare grids of cards. */
    card?: boolean;
    /** Removes body padding — for tables that draw their own */
    flush?: boolean;
    footer?: React.ReactNode;
}

/**
 * A titled block of content. Gives every screen the same heading rhythm
 * instead of ad-hoc `<h2 className="text-lg font-semibold mb-4">`.
 */
export const PageSection: React.FC<PageSectionProps> = ({
    title,
    description,
    actions,
    card = true,
    flush = false,
    footer,
    className,
    children,
    ...props
}) => (
    <section className={cn("w-full min-w-0", className)} {...props}>
        {(title || actions) && (
            <div className="flex items-end justify-between gap-3 mb-3 min-w-0">
                <div className="min-w-0">
                    {title && (
                        <h2 className="text-title-sm font-semibold text-content tracking-tight">{title}</h2>
                    )}
                    {description && (
                        <p className="text-meta text-content-muted mt-0.5 leading-relaxed">{description}</p>
                    )}
                </div>
                {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
            </div>
        )}

        {card ? (
            <div className="rounded-xl bg-surface border border-line shadow-card overflow-hidden">
                <div className={cn(!flush && "p-4 sm:p-5")}>{children}</div>
                {footer && (
                    <div className="px-4 sm:px-5 py-3 border-t border-line-subtle bg-surface-muted">{footer}</div>
                )}
            </div>
        ) : (
            <>
                {children}
                {footer}
            </>
        )}
    </section>
);

export const Panel: React.FC<React.HTMLAttributes<HTMLDivElement> & { flush?: boolean }> = ({
    flush,
    className,
    children,
    ...props
}) => (
    <div
        className={cn(
            "rounded-xl bg-surface border border-line shadow-card overflow-hidden min-w-0",
            !flush && "p-4 sm:p-5",
            className
        )}
        {...props}
    >
        {children}
    </div>
);

export default Page;
